import { useMemo } from "react";
import { CalendarDays, ChevronRight, Phone, Sun } from "lucide-react";

// 12 monthly tips tuned for Central Texas yards (Bermuda/St. Augustine)
const TIPS = [
  {
    month: "January",
    season: "Winter",
    title: "Sharpen tools, plan the season",
    body: "Sharpen mower blades, service your trimmer, and map out a fertilizer schedule. Apply a dormant pre-emergent if you haven't already to head off spring crabgrass.",
  },
  {
    month: "February",
    season: "Late Winter",
    title: "Pre-emergent window closes",
    body: "Get your final pre-emergent down before soil temps hit 55°F. Inspect beds for early weeds and pull by hand while roots are weak.",
  },
  {
    month: "March",
    season: "Early Spring",
    title: "Wake the lawn up",
    body: "First mow at the lower setting to remove dead tips. Aerate compacted yards, top-dress thin spots, and water deeply once a week.",
  },
  {
    month: "April",
    season: "Spring",
    title: "Feed for the season",
    body: "Apply a balanced spring fertilizer (24-0-6 or similar). Mow weekly, raise the deck slightly, and edge beds for a crisp, ironclad finish.",
  },
  {
    month: "May",
    season: "Late Spring",
    title: "Mulch & mow tall",
    body: "Refresh mulch beds 2-3″ deep to lock in moisture before the heat. Raise mower height — taller grass shades soil and beats weeds.",
  },
  {
    month: "June",
    season: "Early Summer",
    title: "Water deep, not often",
    body: "Switch to deep, infrequent watering (1-1.5″ once a week) early morning. Spot-spray broadleaf weeds and watch for chinch bugs in St. Augustine.",
  },
  {
    month: "July",
    season: "Summer",
    title: "Mow high, sharpen often",
    body: "Keep blades sharp — torn grass tips brown out fast in Texas heat. Mow at 3.5-4″ and never remove more than one-third of the blade.",
  },
  {
    month: "August",
    season: "Late Summer",
    title: "Iron boost for deep green",
    body: "Apply an iron-fortified summer feed to keep color through 100°F days. Check sprinkler coverage — dry spots burn out in a single afternoon.",
  },
  {
    month: "September",
    season: "Early Fall",
    title: "Overseed shaded areas",
    body: "Cooler nights are perfect for overseeding fescue under live oaks. Drop a fall pre-emergent to stop winter weeds before they germinate.",
  },
  {
    month: "October",
    season: "Fall",
    title: "Root builder & cleanup",
    body: "Apply a fall root-building fertilizer (10-20-20). Clear leaves weekly — mats smother grass and invite fungus.",
  },
  {
    month: "November",
    season: "Late Fall",
    title: "Last mow & winterize",
    body: "Lower the deck slightly for the final cut. Drain hoses, blow out irrigation, and prune dormant shrubs for shape.",
  },
  {
    month: "December",
    season: "Winter",
    title: "Protect & plan",
    body: "Mulch around tender plants before freezes. Schedule a January pre-emergent and book recurring service so spring doesn't catch you flat-footed.",
  },
];

export default function SeasonalTip() {
  const idx = useMemo(() => new Date().getMonth(), []);
  const tip = TIPS[idx];
  const next = TIPS[(idx + 1) % 12];

  return (
    <section
      id="seasonal-tip"
      data-testid="seasonal-tip-section"
      className="py-24 md:py-32 bg-background border-t border-border"
    >
      <div className="container-ic">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: heading */}
          <div className="lg:col-span-5">
            <div className="overline mb-4">
              <Sun className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
              Ironclad Yard Tips
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              Pristine,
              <br />
              <span className="text-primary">all year long.</span>
            </h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-xl">
              A fresh, season-aware tip every month — tuned for Central Texas lawns.
              Your yard tells you what it needs; we help you listen.
            </p>
            <a
              href="#booking"
              className="btn-outline mt-8"
              data-testid="seasonal-tip-cta"
            >
              Want us to handle it? <ChevronRight className="h-4 w-4" />
            </a>
          </div>

          {/* Right: current month tip card */}
          <div className="lg:col-span-7 space-y-6">
            <article
              className="border border-primary/40 bg-card/40 p-8 md:p-10"
              data-testid="current-tip-card"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <CalendarDays className="h-5 w-5 text-primary" />
                  <span className="overline">
                    {tip.month} · {tip.season}
                  </span>
                </div>
                <span className="text-[10px] tracking-widest text-muted-foreground uppercase">
                  This Month's Tip
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight leading-tight">
                {tip.title}
              </h3>
              <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
                {tip.body}
              </p>
            </article>

            <article
              className="border border-border bg-background p-6 md:p-8"
              data-testid="next-tip-card"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="overline text-muted-foreground">
                  Up Next · {next.month}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <h4 className="font-display text-xl font-bold uppercase tracking-tight">
                {next.title}
              </h4>
              <p className="mt-2 text-sm text-muted-foreground">
                {next.body}
              </p>
            </article>

            <a
              href="tel:5129480676"
              className="flex items-center justify-between border border-border p-5 hover:border-primary transition-colors"
              data-testid="seasonal-tip-call"
            >
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  Stuck between seasons?
                </div>
                <div className="font-display text-xl font-bold mt-1">
                  Call us — 512-948-0676
                </div>
              </div>
              <Phone className="h-5 w-5 text-primary" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
